import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Share2, Check } from "lucide-react";
import { toast } from "sonner";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { ProductGrid, ProductBadges } from "../components/ProductCard";
import { QtyControl } from "../components/CartDrawer";
import { useCart } from "../context/CartContext";
import { api, BACKEND, formatPrice, imgUrl } from "../lib/api";
import { useSeo } from "../lib/hooks";

const Pyramid = ({ p }) => {
  const tiers = [["Notes de tête", p.notes_top], ["Notes de cœur", p.notes_heart], ["Notes de fond", p.notes_base]];
  const hasNotes = tiers.some(([, n]) => n?.length);
  return (
    <div className="mt-10" data-testid="olfactory-pyramid">
      <p className="eyebrow mb-4">Pyramide olfactive</p>
      {hasNotes ? (
        <div className="grid grid-cols-3 border border-[#C9A45C]/20 divide-x divide-[#C9A45C]/20">
          {tiers.map(([label, notes]) => (
            <div key={label} className="p-4">
              <p className="text-[10px] tracking-[0.2em] uppercase text-[#F8F6F1]/45 mb-2">{label}</p>
              <p className="font-display text-lg leading-snug">{notes?.length ? notes.join(", ") : "—"}</p>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-[#F8F6F1]/55 border border-dashed border-[#C9A45C]/25 p-4" data-testid="notes-unconfirmed">Notes olfactives non communiquées pour le moment.</p>
      )}
      {hasNotes && p.is_example && <p className="mt-2 text-xs text-[#F8F6F1]/40">Notes données à titre d'exemple pour la démonstration.</p>}
    </div>
  );
};

const Regulatory = ({ r = {} }) => {
  const rows = [["ingredients", "Composition (liste INCI)"], ["precautions", "Précautions d'emploi"], ["manufacturer", "Fabricant / personne responsable"], ["other", "Autres mentions obligatoires"]];
  return (
    <Accordion type="single" collapsible className="mt-10 border-t border-[#C9A45C]/15" data-testid="regulatory-info">
      {rows.map(([k, label]) => (
        <AccordionItem key={k} value={k} className="border-[#C9A45C]/15">
          <AccordionTrigger className="text-xs tracking-[0.2em] uppercase hover:no-underline hover:text-[#C9A45C]" data-testid={`regulatory-${k}`}>{label}</AccordionTrigger>
          <AccordionContent className="text-sm text-[#F8F6F1]/65 whitespace-pre-line">{r[k] || "Information à compléter avec les données officielles du produit."}</AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
};

export default function ProductPage() {
  const { slug } = useParams();
  const { add } = useCart();
  const [data, setData] = useState(null);
  const [active, setActive] = useState(0);
  const [qty, setQty] = useState(1);

  useEffect(() => {
    setData(null); setActive(0); setQty(1);
    api.get(`/products/${slug}`).then((r) => setData(r.data)).catch(() => setData(false));
  }, [slug]);

  const p = data?.product;
  const shareUrl = `${BACKEND}/api/share/parfum/${slug}`;
  useSeo({
    title: p ? p.seo_title || p.name : "Parfum",
    description: p ? p.seo_description || `${p.name} à ${formatPrice(p.price)} chez NasDeal. Le luxe à portée de tous.` : undefined,
    image: p ? `${BACKEND}/api/share/image/${slug}.jpg` : undefined,
    jsonLd: p && {
      "@context": "https://schema.org", "@type": "Product", name: p.name, image: (p.images || []).map(imgUrl), description: p.description,
      brand: { "@type": "Brand", name: "NasDeal" },
      offers: { "@type": "Offer", priceCurrency: "EUR", price: p.price, availability: p.stock > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock", url: window.location.href },
    },
  });

  if (data === false) return <div className="py-32 text-center"><p className="font-display text-3xl">Parfum introuvable</p><Link to="/boutique" className="btn-outline-gold mt-8">Retour à la boutique</Link></div>;
  if (!p) return <div className="max-w-7xl mx-auto px-5 py-20 grid lg:grid-cols-2 gap-12"><div className="aspect-[4/5] bg-[#141414] animate-pulse" /><div className="space-y-4"><div className="h-10 w-2/3 bg-[#141414]" /><div className="h-6 w-1/3 bg-[#141414]" /></div></div>;

  const share = async () => {
    const shareData = { title: `${p.name} | NasDeal`, text: `${p.name} — ${formatPrice(p.price)} chez NasDeal`, url: shareUrl };
    try {
      if (navigator.share) await navigator.share(shareData);
      else { await navigator.clipboard.writeText(shareUrl); toast.success("Lien de partage copié"); }
    } catch { /* annulé */ }
  };
  const images = p.images?.length ? p.images : [null];

  return (
    <div data-testid="product-page">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-8 pb-16">
        <nav className="text-[11px] tracking-[0.2em] uppercase text-[#F8F6F1]/45 mb-8" aria-label="Fil d'Ariane">
          <Link to="/" className="hover:text-[#C9A45C]">Accueil</Link> / {data.category && <><Link to={`/categorie/${data.category.slug}`} className="hover:text-[#C9A45C]">{data.category.name}</Link> / </>}<span className="text-[#C9A45C]">{p.name}</span>
        </nav>
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-7 flex flex-col-reverse sm:flex-row gap-4">
            {images.length > 1 && (
              <div className="flex sm:flex-col gap-3 overflow-x-auto no-scrollbar">
                {images.map((img, i) => (
                  <button key={i} onClick={() => setActive(i)} className={`shrink-0 h-20 w-16 sm:h-24 sm:w-20 overflow-hidden border transition-colors ${i === active ? "border-[#C9A45C]" : "border-transparent opacity-60 hover:opacity-100"}`} data-testid={`product-thumb-${i}`}>
                    <img src={imgUrl(img)} alt={`${p.name} vue ${i + 1}`} className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            )}
            <div className="relative flex-1 aspect-[4/5] overflow-hidden bg-[#141414] group">
              <ProductBadges p={p} />
              <img src={imgUrl(images[active])} alt={p.name} className="h-full w-full object-cover transition-transform duration-[1500ms] group-hover:scale-110" data-testid="product-main-image" />
            </div>
          </div>

          <div className="lg:col-span-5 lg:sticky lg:top-32 self-start">
            {p.is_example && <p className="inline-block mb-4 border border-[#C9A45C]/40 text-[#C9A45C] text-[10px] tracking-[0.25em] uppercase px-3 py-1" data-testid="product-example-notice">Produit d'exemple — démonstration</p>}
            <p className="eyebrow">{data.category?.name}</p>
            <h1 className="font-display text-4xl sm:text-5xl mt-3 leading-tight" data-testid="product-name">{p.name}</h1>
            <div className="mt-5 flex items-baseline gap-4">
              <p className="font-display text-3xl text-[#C9A45C]" data-testid="product-price">{formatPrice(p.price)}</p>
              <p className="text-sm text-[#F8F6F1]/55" data-testid="product-volume">{p.volume_ml ? `${p.volume_ml} ml` : "Contenance à confirmer"}</p>
            </div>
            {p.brand && <p className="mt-2 text-sm text-[#F8F6F1]/60" data-testid="product-brand">Marque : {p.brand}</p>}
            <div className="mt-6 grid grid-cols-2 gap-px bg-[#C9A45C]/15 border border-[#C9A45C]/15 text-sm">
              <div className="bg-[#0B0B0B] p-4"><p className="text-[10px] tracking-[0.2em] uppercase text-[#F8F6F1]/45">Famille olfactive</p><p className="mt-1" data-testid="product-family">{p.family || "—"}</p></div>
              <div className="bg-[#0B0B0B] p-4"><p className="text-[10px] tracking-[0.2em] uppercase text-[#F8F6F1]/45">Disponibilité</p>
                <p className={`mt-1 ${p.stock > 0 ? "text-emerald-400/90" : "text-red-400/90"}`} data-testid="product-availability">{p.stock > 0 ? (p.stock <= 3 ? `Plus que ${p.stock} en stock` : "En stock") : "Épuisé"}</p></div>
            </div>
            <p className="mt-6 text-[#F8F6F1]/70 leading-relaxed text-sm sm:text-base whitespace-pre-line" data-testid="product-description">{p.description}</p>

            <div className="mt-8 flex gap-3 items-stretch">
              <QtyControl value={qty} onChange={setQty} max={Math.max(1, Math.min(p.stock, 20))} testid="product-qty" />
              <button className="btn-gold flex-1" disabled={p.stock <= 0} onClick={() => add(p, qty)} data-testid="product-add-to-cart">
                {p.stock > 0 ? "Ajouter au panier" : "Épuisé"}
              </button>
              <button onClick={share} className="border border-[#C9A45C]/30 px-3 hover:border-[#C9A45C] text-[#C9A45C]" aria-label="Partager" data-testid="product-share-button"><Share2 className="h-4 w-4" /></button>
            </div>
            <ul className="mt-6 space-y-2 text-xs text-[#F8F6F1]/55">
              {["Paiement sécurisé par Stripe", "Livraison à domicile, en point relais ou retrait gratuit", "Commande sans création de compte"].map((t) => (
                <li key={t} className="flex items-center gap-2"><Check className="h-3.5 w-3.5 text-[#C9A45C]" />{t}</li>
              ))}
            </ul>
            <Pyramid p={p} />
            <Regulatory r={p.regulatory} />
          </div>
        </div>
      </div>
      {data.similar.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16 border-t border-[#C9A45C]/10" data-testid="similar-products">
          <p className="eyebrow mb-3">Vous aimerez aussi</p>
          <h2 className="font-display text-3xl sm:text-4xl mb-10">Parfums similaires</h2>
          <ProductGrid products={data.similar} testid="similar-grid" />
        </section>
      )}
    </div>
  );
}
